import { Button } from "@/components/ui/button.tsx"
import { useEffect, useState, useRef } from "preact/hooks"
import { ZoomIn, ZoomOut, RotateCw, Download, ChevronDown } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx"
import { format } from "date-fns"
import { capitalCase } from "change-case"

export interface FileViewerFile {
  display_name: string
  date?: string
  s3_uri: string
  actor_name?: string
}

interface FileViewerProps {
  s3Uri: string
  fileName?: string
  allFiles?: FileViewerFile[]
  onFileSelect?: (file: FileViewerFile) => void
}

export function FileViewer({ s3Uri, fileName, allFiles, onFileSelect }: FileViewerProps) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isLoading, setIsLoading] = useState(true)
  const containerRef = useRef<HTMLDivElement>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const isPdf = s3Uri.toLowerCase().endsWith(".pdf")
  const isImage = [".png", ".jpg", ".jpeg", ".gif", ".webp", ".tif", ".tiff"].some(ext => s3Uri.toLowerCase().endsWith(ext))

  useEffect(() => {
    if (isPdf) {
      const url = `/api/_s3?s3_uri=${encodeURIComponent(s3Uri)}`
      setPdfUrl(url)
    } else {
      setPdfUrl(null)
    }
    setIsLoading(false)
  }, [s3Uri, isPdf])

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3))
  }

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 1.0))
  }

  const handleResetZoom = () => {
    setZoom(1)
    setPosition({ x: 0, y: 0 })
    setRotation(0)
  }

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360)
  }

  const handleMouseDown = (e: MouseEvent) => {
    // Only start dragging if clicking on the container background, not the iframe
    if (e.target === iframeRef.current || (e.target as Element)?.closest('iframe')) {
      return
    }
    e.preventDefault()
    setIsDragging(true)
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    })
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return
    e.preventDefault()
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    })
  }

  const handleMouseUp = (e: MouseEvent) => {
    setIsDragging(false)
  }

  const handleWheel = (e: WheelEvent) => {
    e.preventDefault()
    if (e.ctrlKey || e.metaKey) {
      // Zoom with Ctrl/Cmd + wheel
      const delta = e.deltaY > 0 ? -0.1 : 0.1
      setZoom(prev => Math.max(1.0, Math.min(3, prev + delta)))
    } else {
      // Pan with wheel
      setPosition(prev => ({
        x: prev.x - e.deltaX,
        y: prev.y - e.deltaY
      }))
    }
  }

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.addEventListener('mousemove', handleMouseMove)
    container.addEventListener('mouseup', handleMouseUp)
    container.addEventListener('wheel', handleWheel, { passive: false })

    return () => {
      container.removeEventListener('mousemove', handleMouseMove)
      container.removeEventListener('mouseup', handleMouseUp)
      container.removeEventListener('wheel', handleWheel)
    }
  }, [isDragging, dragStart])

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-2"></div>
          <div>Loading file...</div>
        </div>
      </div>
    )
  }

  if (isImage) {
    // Scanned remittances and backup photos arrive as JPG/PNG/TIFF as often as PDF, and they were
    // falling through to "No preview available" — which makes checking a parse against its source
    // impossible without downloading the file first.
    return (
      <div className="w-full h-full overflow-auto bg-gray-50 flex items-start justify-center p-2">
        <img
          src={`/api/_s3?s3_uri=${encodeURIComponent(s3Uri)}`}
          alt={fileName || "Document"}
          className="max-w-full h-auto object-contain"
        />
      </div>
    )
  }

  if (!isPdf) {
    const displayName = fileName || s3Uri.split("/").pop() || "File"
    return (
      <div className="w-full h-full flex flex-col items-center justify-center">
        <div className="text-center mb-4">
          <h3 className="text-lg font-semibold mb-2">{displayName}</h3>
          <p className="text-gray-600 mb-4">No preview available.</p>
          <Button
            variant="tertiary"
            onClick={() => window.open(`/api/_s3?s3_uri=${encodeURIComponent(s3Uri)}`, "_blank")}
            title="Download file"
          >
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-2 border-b">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            // @ts-ignore
            size="sm"
            onClick={handleZoomOut}
            disabled={zoom <= 1.0}
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium min-w-[60px] text-center">
            {Math.round(zoom * 100)}%
          </span>
          <Button
            variant="outline"
            // @ts-ignore
            size="sm"
            onClick={handleZoomIn}
            disabled={zoom >= 3}
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            // @ts-ignore
            size="sm"
            onClick={handleRotate}
            title="Rotate 90°"
          >
            <RotateCw className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            // @ts-ignore
            size="sm"
            onClick={handleResetZoom}
            title="Reset view"
          >
            Reset
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          {allFiles && allFiles.length > 1 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline"
                  // @ts-ignore
                  size="sm" className="text-sm">
                  {fileName}
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                {allFiles.map((file, index) => (
                  <DropdownMenuItem
                    key={index}
                    onClick={() => onFileSelect?.(file)}
                    className="flex flex-col items-start p-3 cursor-pointer"
                  >
                    <div className="font-medium text-sm">{file.display_name}</div>
                    <div className="flex gap-4 text-xs text-gray-500 mt-1">
                      {file.actor_name && (
                        <span>{capitalCase(file.actor_name)}</span>
                      )}
                      {file.date && (
                        <span>{format(new Date(file.date), "PP")}</span>
                      )}
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button
            variant="outline"
            // @ts-ignore
            size="sm"
            onClick={() => window.open(`/api/_s3?s3_uri=${encodeURIComponent(s3Uri)}`, "_blank")}
            title="Open in new tab"
          >
            <Download className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* File Content */}
      <div
        ref={containerRef}
        className="flex-1 overflow-auto relative"
        onMouseDown={handleMouseDown}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <div
          className="flex items-center justify-center"
          style={{
            width: `${100 * zoom}%`,
            height: `${100 * zoom}%`,
            transform: `translate(${position.x}px, ${position.y}px) rotate(${rotation}deg)`,
            transformOrigin: 'center center',
            transition: isDragging ? 'none' : 'transform 0.1s ease-out'
          }}
        >
          {pdfUrl && (
            <iframe
              ref={iframeRef}
              src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`}
              className="border-0"
              title={fileName}
              style={{
                width: '100%',
                height: '100%',
                pointerEvents: 'auto',
                background: 'white'
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}
